import type { Database } from "../../__definitions__/database-definitions";

type ProgramTreeView = Database["public"]["Views"]["program_tree"]["Row"];

/** The columns of the program_tree view that can be null. */
const NULLABLE: ReadonlySet<string> = new Set(["parent_id", "description"]);

/** A row of the program_tree view. Its types make each column nullable, but only NULLABLE are. */
export type ProgramTreeRow = {
  [K in keyof ProgramTreeView]: K extends "parent_id" | "description"
    ? ProgramTreeView[K]
    : NonNullable<ProgramTreeView[K]>;
};

const isProgramTreeRow = (row: ProgramTreeView): row is ProgramTreeRow =>
  Object.entries(row).every(([key, value]) => NULLABLE.has(key) || value !== null);

export interface ProgramNode extends ProgramTreeRow {
  /** The visible children, sorted by title. */
  children: ProgramNode[];
}

export interface ProgramTree {
  /** The visible top-level programs, sorted by title. */
  roots: ProgramNode[];
  /** Each program, visible or not, so that a URL to a hidden program still finds it. */
  byId: Map<string, ProgramNode>;
}

const byTitle = (a: ProgramNode, b: ProgramNode): number =>
  a.title.localeCompare(b.title, undefined, { sensitivity: "base" });

/**
 * Builds the tree that the switcher and the tabs show. Each level is sorted by title. A program that
 * is not visible is left out of its parent's children, so its whole branch is hidden. A program whose
 * parent is not in the rows is at the top level. A program with an empty title gets "Untitled", so
 * that its row is not blank.
 */
export const buildProgramTree = (rows: ProgramTreeView[]): ProgramTree => {
  const byId = new Map(
    rows
      .filter(isProgramTreeRow)
      .map((row) => [
        row.id,
        { ...row, title: row.title.trim() ? row.title : "Untitled", children: [] as ProgramNode[] },
      ]),
  );
  const roots: ProgramNode[] = [];

  for (const node of byId.values()) {
    if (!node.visible) continue;
    const parent = node.parent_id ? byId.get(node.parent_id) : undefined;
    (parent ? parent.children : roots).push(node);
  }

  for (const node of byId.values()) node.children.sort(byTitle);
  roots.sort(byTitle);

  return { roots, byId };
};

/** The program and each program above it, from the top down. */
export const programPath = (tree: ProgramTree, id: string): ProgramNode[] => {
  const path: ProgramNode[] = [];
  const seen = new Set<string>();
  let node = tree.byId.get(id);

  while (node && !seen.has(node.id)) {
    seen.add(node.id);
    path.unshift(node);
    node = node.parent_id ? tree.byId.get(node.parent_id) : undefined;
  }

  return path;
};

/** "Program" at the top, and "Folder · level 2" and so on below it. */
export const levelText = (depth: number): string =>
  depth === 1 ? "Program" : `Folder · level ${depth}`;

/** Each visible program, depth first in the order the switcher shows them, with its depth. */
export const flattenProgramTree = (tree: ProgramTree): { node: ProgramNode; depth: number }[] => {
  const rows: { node: ProgramNode; depth: number }[] = [];
  const walk = (nodes: ProgramNode[], depth: number): void => {
    for (const node of nodes) {
      rows.push({ node, depth });
      walk(node.children, depth + 1);
    }
  };

  walk(tree.roots, 0);
  return rows;
};
